<template>
  <div class="h-100">
    <div v-if="isLoading" class="d-flex justify-content-center align-items-center h-100 w-100">
      <b-spinner type="grow" class="align-middle" variant="secondary" />
    </div>
    <stream-miu-page-container
      v-else
      :next-loading="nextLoading"
      :exist-extra-block="hasEvaluation"
      @onPagesCompleted="handlePagesCompleted"
      @backToStream="backToStream"
    >
      <template #extra-block v-if="hasEvaluation">
        <evaluation-block @onFeedback="onFeedback" />
      </template>
    </stream-miu-page-container>
    <soft-skills-glossary
      v-if="!isLoading && !isTablet && elementSoftSkills"
      :elementSoftSkills="elementSoftSkills"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import StreamPageContainer from "../organisms/stream-page/StreamPageContainer";
import StreamMiuPageContainer from "../organisms/stream-page/StreamMiuPageContainer";
import navMixin from "../mixins/navMixin";
import streamMixin from "../mixins/streamMixin";
import EvaluationBlock from "../organisms/stream-page/blocks/EvaluationBlock";
import ABTestWrapper from "../molecules/ABTestWrapper";
import SoftSkillsGlossary from "../organisms/sidebars/SoftSkillsGlossary";
import mobileCheckMixin from "../mixins/mobileCheckMixin";

export default {
  components: {
    EvaluationBlock,
    StreamPageContainer,
    ABTestWrapper,
    StreamMiuPageContainer,
    SoftSkillsGlossary,
  },
  name: "StreamMiuContainer",
  mixins: [navMixin, streamMixin, mobileCheckMixin],
  data() {
    return {
      nextLoading: false,
      prevRouteName: "",
      feedbackData: null,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRouteName = from.name;
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.prevRouteName = "";
    next();
  },
  methods: {
    ...mapActions({
      loadMiu: "streams/loadElement",
      loadMoodsList: "moodsAndReactions/loadMoodsList",
      loadReactionList: "moodsAndReactions/loadReactionList",
      submitActivity: "streams/submitElement",
      logElementActivity: "streams/logElementActivity",
    }),
    ...mapMutations({
      setBlocksPerPage: "streams/setBlocksPerPage",
      setCurrentPage: "streams/setCurrentPage",
    }),
    async handlePagesCompleted() {
      await this.completeMiu();

      trackEvent("StreamElementComplete", {
        streamId: this.elementId,
        aleId: this.elementType,
        miuId: this.$route.params.id,
      });

      if (this.nextElement) {
        this.handleNext();
      } else {
        this.backToStreamDasboard();
      }
    },
    backToStreamDasboard() {
      this.$router.push({ name: "stream", id: this.$route.params.id });
    },
    backToStream() {
      if (this.backButtonRoutes.includes(this.prevRouteName)) {
        this.$router.push({ name: this.prevRouteName });
      } else {
        this.$router.push({ name: "stream", id: this.$route.params.id });
      }
    },
    async completeMiu() {
      this.nextLoading = true;
      await this.completeActivity(
        this.elementId,
        this.elementType,
        this.$route.params.id,
        this.elementSoftSkills
      );

      if (this.feedbackData) {
        await this.sendFeedbackData();
      }

      this.logElementActivity();
      this.nextLoading = false;
      return Promise.resolve();
    },
    async sendFeedbackData() {
      let feedbackActivityData = [];

      let feedbackMain = {
        action: "evaluated",
        objectId: this.elementId,
        objectType: this.elementType,
      };

      let feedbackDetails = [];

      feedbackDetails.push({ key: "stream_id", value: this.$route.params.id });
      feedbackDetails.push({ key: "ale_id", value: this.$route.params.aleId });
      feedbackDetails.push({ key: "localisation", value: this.$route.params.localisation });
      feedbackDetails.push({ key: "element_type", value: this.elementType });
      feedbackDetails.push({ key: "element_id", value: this.$route.params.elementId });
      feedbackDetails.push({ key: "feedback", value: this.feedbackData.feedback });
      feedbackDetails.push({ key: "feedback_text", value: this.feedbackData.text });

      feedbackActivityData.push({ main: feedbackMain, details: feedbackDetails });
      await this.submitActivity({ data: feedbackActivityData });
    },
    handleNext() {
      if (this.nextElement.final) {
        this.openTransitionPage("ale_completed");
      } else if (this.nextElement.break) {
        this.openTransitionPage("break_time");
      } else {
        this.openNextMiu();
      }
    },
    openNextMiu() {
      const routeParams = this.$route.params;
      const route = this.itemRoute(
        routeParams.id,
        routeParams.aleId,
        routeParams.localisation,
        this.nextElement,
        this.nextElement.completed ? "review" : "instructions"
      );
      this.$router.push(route);
      this.setCurrentPage(1);
      this.loadMiuContent();
    },
    openTransitionPage(state = "") {
      this.$router.push({
        name: "stream-miu-completed",
        params: { ...this.$route.params, state: state },
      });
    },
    async loadMiuContent() {
      await this.loadMiu({ ...this.$route.params, elementType: "miu" });
      this.setBlocksPerPage(1);
      this.feedbackData = null;

      trackEvent("StreamElementView", {
        streamId: this.$route.params.id,
        aleId: this.$route.params.aleId,
        miuId: Number(this.$route.params.elementId),
        currentPage: this.currentPage,
        elementType: "miu",
        numberOfBlock: this.currentBlocks.length,
        numberOfBlockWithReaction: this.currentBlocks.filter((b) => b.reactions === true).length,
      });
    },
    onFeedback(feedbackData) {
      this.feedbackData = feedbackData;
    },
  },
  computed: {
    ...mapGetters({
      isLoading: "streams/getLoadingPageState",
      pagesLength: "streams/getPagesLength",
      currentPage: "streams/getCurrentPage",
      streamInfo: "streams/getStreamInfo",
      elementId: "streams/getCurrentElementId",
      elementType: "streams/getCurrentElementType",
      nextElement: "streams/getNextElement",
      blocks: "streams/getPageBlocks",
      miuType: "streams/getMiuType",
      elementSoftSkills: "streams/getElementSoftSkill",
      miuTestData: "streams/getMiuTestData",
    }),
    hasEvaluation() {
      return this.miuType ? this.miuType.has_evaluation : false;
    },
    checkMiuTestA() {
      return this.miuTestData.isTypeA && this.miuTestData.isTested;
    },
    currentBlocks() {
      return this.blocks(this.currentPage);
    },
  },
  mounted() {
    this.loadMoodsList();
    this.loadReactionList();
    this.loadMiuContent();
  },
};
</script>
